/* eslint-disable no-shadow */
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';

import AddEventToCalendar from '../../../../../components/AddToCalander';
import AdWrapper from '../../../../../components/Ads/AdWrapper';
import SportLabel from '../../../../../components/cards/CardComponents/SportLabel';
import Popup from '../../../../../components/Popup';
import TabBar from '../../../../../components/TabBar';
import PaidHitRegistrationButtonV2 from './PaidHitRegistrationButton';
import RegistrationCtaV3 from './RegistrationCta';

import * as postScoreActions from '../../../../../actions/post-score-actions';
import { useAdDynamicConfig } from '../../../../../components/hooks/useAdDynamicConfig';
import useIsMobile from '../../../../../components/hooks/useIsMobile';
import { useStatsig } from '../../../../../components/hooks/useStatsig';
import { getNewClubPath, isActive as isClubActive } from '../../../../../utils/clubs';
import { EventProfileTabIdentifiers } from '../../../../../utils/constants';
import { eventStateTypeIds, sportTypeIds, utrTypeIds } from '../../../../../utils/eventConstants';
import { EventTypes, getDivisionCountsView, getPaidHitterExperience, hasQualifyingAcceptanceBucket, isEventType, shouldShowDivisionCounts, showCompleteRegistrationButton, showUnder18 as showUnder18ForEvent, validateUserRegisteredNonEntryFeeDivisions } from '../../../../../utils/events';
import { getDivisionCounts } from '../../../../../actions/event-profile-actions';
import { splitTitle } from '../../../../../utils/formatting';
import { AdTypes, getLabelByTypeId } from '../../../../../utils/types';
import StatsigLink from '../../../../../components/Links/StatsigLink';

import config from '../../../../../../config';

import styles from '../../../event.scss';

function EventProfileHeaderV3(props) {
    const {
        isCurrentMemberAdmin,
        collapsed,
        tabs,
        previewOnly,
        sportTypeId,
        handleTabClick,
        handleRegClick,
        showShareUrlPopup,
        isRegistrationIncomplete,
        hasPendingAdminInvitation,
        handleFindPlayerClick,
        isPttEvent,
        isCurrentMemberClubOrganizer,
    } = props;

    const dispatch = useDispatch();
    const auth = useSelector(({ auth }) => auth);
    const coreUI = useSelector(({ coreUI }) => coreUI);
    const { event } = useSelector(({ eventProfile }) => {
        const { event } = eventProfile;
        return {
            event,
        };
    });
    const divisionCounts = useSelector(({ eventProfile }) => eventProfile.divisionCounts);

    const showUnder18 = showUnder18ForEvent(event);

    useEffect(() => {
        if (isPttEvent && isCurrentMemberClubOrganizer && showUnder18 && event.data?.id) {
            dispatch(getDivisionCounts(event.data.id));
        }
    }, [event.data?.id, isPttEvent, isCurrentMemberClubOrganizer, showUnder18]);

    const [showSafeSportPopup, setShowSafeSportPopup] = useState(false);
    const { tms: { surfaceTypes, environmentTypes, division: { formatTypes } } } = coreUI.types;

    const { isMobileWidth } = useIsMobile();
    const { showAds, eventProfileConfig } = useAdDynamicConfig();
    const { checkGate, featureGates } = useStatsig();
    const showLeaderboardAd = showAds && isMobileWidth && eventProfileConfig?.showLeaderboard;
    const isLiveScoresEnabled = checkGate(featureGates.WEB_PTT_LIVE_SCORES);

    let showPlayerCount = true;
    if (!isCurrentMemberAdmin && (event.data && !event.data.playerCountVisibility)) {
        showPlayerCount = false;
    }

    const userIsPowered = auth.user?.isPowered;
    const isRegistrationClosed = event.data.registrationStateId === eventStateTypeIds.REGISTRATION_CLOSED;
    const isFlexLeagueEvent = tabs.map(obj => obj.title).includes('Matchups') || isEventType(event.data, EventTypes.FLEX_LEAGUE);
    const isPaidHitEvent = isEventType(event.data, EventTypes.PAID_HIT);
    const paidHitsData = event.data.paidHits;
    const playerExperienceTypesObject = coreUI?.types.playerExperienceTypes;
    const coachExperienceTypesObject = coreUI?.types.coachingExperienceTypes;
    const invitedText = 'YOU’RE INVITED';
    const registeredText = 'YOU’RE REGISTERED';
    const nonEntryFeeDivisions = event.data?.eventDivisions.filter(division => division.isEventEntryFeeDivision === false);
    const hasPlayersRegistered = nonEntryFeeDivisions?.some(division => division.registeredCount > 0);
    const isUserRegisteredForEvent = event?.data?.currentMember?.isPlaying && hasPlayersRegistered && validateUserRegisteredNonEntryFeeDivisions(nonEntryFeeDivisions, event?.data);
    const showCompleteRegistrationBtn = (showCompleteRegistrationButton(event.data?.currentMember?.registeredDivisions || [], event.data?.eventDivisions) || isRegistrationIncomplete || hasPendingAdminInvitation) && !isRegistrationClosed;
    let statusMessage = '';
    if (showCompleteRegistrationBtn) {
        statusMessage = invitedText;
    } else if (isUserRegisteredForEvent) {
        statusMessage = registeredText;
    }
    useEffect(() => {
        if (isFlexLeagueEvent) {
            dispatch(postScoreActions.setRound({ redirectUrlPage: 'FlexLeagues' }));
        } else {
            dispatch(postScoreActions.setRound({ redirectUrlPage: 'EventDeskPost' }));
        }
    }, []);

    const renderDivisionEnvironmentsAndSurfaces = () => {
        const environmentsToRender = event.data.eventDivisions.reduce((acc, curr) => {
            if (curr.environments) {
                curr.environments.forEach(env => {
                    const environmentLabel = getLabelByTypeId(environmentTypes, env.environmentId);
                    if (!acc.includes(environmentLabel)) {
                        acc.push(environmentLabel);
                    }
                });
            }
            return acc;
        }, []);

        const surfacesToRender = event.data.eventDivisions.reduce((acc, curr) => {
            if (curr.surfaces) {
                curr.surfaces.forEach(surface => {
                    const surfaceLabel = getLabelByTypeId(surfaceTypes, surface.surfaceId);
                    if (!acc.includes(surfaceLabel)) {
                        acc.push(surfaceLabel);
                    }
                });
            }
            return acc;
        }, []);

        const divisionFormatTypes = event.data.eventDivisions.reduce((acc, curr) => {
            if (curr.divisionFormatTypeId && !curr.isEventEntryFeeDivision) {
                const formatTypeLabel = getLabelByTypeId(formatTypes, curr.divisionFormatTypeId);
                if (!acc.includes(formatTypeLabel)) {
                    acc.push(formatTypeLabel);
                }
            }
            return acc;
        }, []);

        return (
            <>
                {environmentsToRender.length > 0 && (
                    <>
                        <span className={`d-none d-md-inline-block middleDot`}>&nbsp;</span>
                        <br className="d-block d-md-none" />
                        <span>
                            {environmentsToRender.join(', ')}
                        </span>
                    </>
                )}
                {surfacesToRender.length > 0 && (
                    <>
                        <span className={`d-none d-md-inline-block middleDot`}>&nbsp;</span>
                        <br className="d-block d-md-none" />
                        <span>
                            {surfacesToRender.join(', ')}
                        </span>
                    </>
                )}
                {divisionFormatTypes.length > 0 && (
                    <>
                        <span className={`d-none d-md-inline-block middleDot`}>&nbsp;</span>
                        <br className="d-block d-md-none" />
                        <span>
                            {divisionFormatTypes.join(' / ')}
                        </span>
                    </>
                )}
            </>
        );
    };

    const validateUserIfRegisteredOnEvent = (eventData) => {
        let isRegistered = false;
        if (eventData?.eventDivisions && eventData?.eventDivisions?.length > 0) {
            if (eventData?.currentMember?.registeredDivisionIds && eventData?.currentMember?.registeredDivisionIds.length > 0) {
                eventData.eventDivisions.map((division) => {
                    isRegistered = division?.registered || eventData.currentMember.registeredDivisions?.some(div => div.id === division.id);
                    return isRegistered;
                });
            }
        }
        return isRegistered;
    };

    const shouldShowAddToCalendar = () => {
        return auth?.user && validateUserIfRegisteredOnEvent(event.data);
    };

    const handleLiveMatchesBannerClick = () => {
        if (handleTabClick) {
            handleTabClick({ tabId: EventProfileTabIdentifiers.SCHEDULE });
        }
    };

    const showDivisionCounts = shouldShowDivisionCounts(showUnder18, divisionCounts);
    const {
        mainCount,
        qualifyingCount,
        alternateCount,
        mainCountUnder18: mainCounUnder18,
        qualifyingCountUnder18: qualifyingCounUnder18,
        alternateCountUnder18: alternateCounUnder18,
        totalUnder18,
    } = getDivisionCountsView(divisionCounts);
    const hasQualifying = hasQualifyingAcceptanceBucket(event);

    return (
        <>
            <div className={`${styles.section} bottom-border header`}>
                <div className="row ">
                    <div className="col-md-12">
                        <div className="event-title">
                            <div className="bg-black text-white">
                                <div className={`container`}>
                                    <div className={`${styles.parentContainer}`}>
                                        <div className={`${styles.leftContainer}`}>
                                            <div className="bg-black text-white ">
                                                <div className=" pl0">
                                                    <div className="d-flex flex-row align-items-center bg-black ">
                                                        <SportLabel className={`mb8 bg-transparent ${sportTypeId === sportTypeIds.TENNIS ? 'tennis-sport-label' : 'pickleball-sport-label'}`} sportTypeId={sportTypeId} />
                                                        <span className={`d-none d-md-inline-block d-flex mb8 mr8  middleDot text-small`} data-testid="event-profile.header.sport-dot-separator">&#x2022;</span>

                                                        {event?.data?.club?.name && !isPaidHitEvent && (
                                                            <p className={`text-highlight2 flex-grow  ${styles.fontSize12}`} data-testid="event-profile.header.club-name-text">
                                                                <StatsigLink
                                                                    className={`text-white  ${styles.fontSize12}`}
                                                                    gate={featureGates.CLUB_LINKS_REDIRECT}
                                                                    enabledTo={`${config.nextAppHost}/${getNewClubPath(event.data.club.tierTypeId)}/${event.data.club.id}`}
                                                                    disabledTo={`/clubs/${event.data.club.id}`}
                                                                >
                                                                    {event.data.club.name}
                                                                </StatsigLink>
                                                            </p>
                                                        )}

                                                    </div>
                                                </div>
                                                <h1 className={`text-semi  bg-black text-white mt8 mb8 ${styles.breakWordV2}`} data-testid="event-profile.header.event-title">
                                                    <span className={`${styles.titleStyle}`}>  {event.data.isDemoEvent ? '[Demo] ' : ''}{splitTitle(event.data.name).firstPart}</span>
                                                    <span className={`unverified ${styles.titleStyle}`}>
                                                        {splitTitle(event.data.name).lastPart}
                                                        {event.data?.hasSafeSportCertifiedOrganizer && (
                                                            <span
                                                                className="ml8 safeSportCertified"
                                                                onClick={() => setShowSafeSportPopup(true)}
                                                                data-testid="event-profile.header.safesport-cert-btn"
                                                            />
                                                        )}
                                                    </span>
                                                </h1>
                                                <div className="pl0 mt16">
                                                    {parseInt(event.data.utrTypeId) === utrTypeIds.SANCTIONED && (
                                                        <span className={`${styles.label}`} data-testid="event-profile.header.verified-event-label">
                                                            <img src={'/img/v4/icon-verified-white.svg'} title="Verified" data-testid="event-profile.header.verified-icon" /> Verified Event
                                                        </span>
                                                    )}
                                                    {event.data?.hasPrizeMoney && (
                                                        <span className={`${styles.label}`} data-testid="event-profile.header.prize-money-label">
                                                            <img src={'/img/v4/icon-dollar-white.svg'} title="Prize money" data-testid="event-profile.header.prize-money-icon" /> Prize Money Event
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {isPaidHitEvent && (
                                                <h6 className="subtitle text-medium text-black text-uppercase" data-testid="event-profile.header.paid-hit-experience-title">
                                                    <span>{getPaidHitterExperience(paidHitsData, playerExperienceTypesObject, coachExperienceTypesObject, userIsPowered, isCurrentMemberAdmin)}</span>
                                                </h6>
                                            )}
                                        </div>
                                        <div className={`${styles.rightContainer}`}>
                                            <div className={`${styles.rightBlock}`}>
                                                {isCurrentMemberAdmin && (
                                                    <div className={`${styles.block}`} data-testid="event-profile.header.registrations-block">
                                                        <div data-testid="event-profile.header.registrations-label">Registrations</div>
                                                        <div className={`${styles.registerationsNumber}`} data-testid="event-profile.header.registrations-count">{event.data?.registrationsCount || 0}</div>
                                                    </div>
                                                )}
                                                {showPlayerCount && (
                                                    <div className={`${styles.block}`} data-testid="event-profile.header.players-block">
                                                        <div data-testid="event-profile.header.players-label">Players</div>
                                                        <div className={`${styles.registerationsNumber}`} data-testid="event-profile.header.players-count">{event.data?.registeredCount || 0}</div>
                                                    </div>
                                                )}
                                                {isPttEvent && isCurrentMemberClubOrganizer && showDivisionCounts && (
                                                    <div className={`${styles.block}`} data-testid="event-profile.header.u18-block">
                                                        <div data-testid="event-profile.header.u18-label">U18</div>
                                                        <div className={`${styles.registerationsNumber}`} data-testid="event-profile.header.u18-count">{totalUnder18 || 0}</div>
                                                    </div>
                                                )}
                                            </div>
                                            {isPttEvent && isCurrentMemberClubOrganizer && showDivisionCounts && (
                                                <>
                                                    <div className={`${styles.divider}`} />
                                                    <div className={`${styles.rightContent}`} data-testid="event-profile.header.division-counts-content">
                                                        <span className={`${styles.content}`} data-testid="event-profile.header.main-count-text">Main: {mainCount || 0}  &#40;U18: {mainCounUnder18 || 0}&#41;</span>
                                                        {hasQualifying && (
                                                            <span className={`${styles.content}`} data-testid="event-profile.header.qualifying-count-text">Qualifying: {qualifyingCount || 0} &#40;U18: {qualifyingCounUnder18 || 0}&#41;</span>
                                                        )}
                                                        <span className={`${styles.content}`} data-testid="event-profile.header.alternate-count-text">Alternate: {alternateCount || 0} &#40;U18: {alternateCounUnder18 || 0}&#41;</span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {isLiveScoresEnabled && !!event.data?.isLive && (
                                <div
                                    className={styles.liveMatchesBanner}
                                    onClick={handleLiveMatchesBannerClick}
                                    role="button"
                                    tabIndex={0}
                                    data-testid="event-profile.header.live-matches-banner"
                                >
                                    <div className={styles.bannerContent}>
                                        <p data-testid="event-profile.header.live-matches-banner-text">
                                            <span className={styles.bannerLink} data-testid="event-profile.header.live-matches-banner-link">Live Matches In Progress</span>
                                            <span className={styles.bannerText} data-testid="event-profile.header.live-matches-banner-subtitle">&nbsp;- Scores will refresh automatically during ongoing matches.</span>
                                        </p>
                                    </div>
                                </div>
                            )}
                            {showLeaderboardAd && (
                                <div className={styles.leaderboardContainer}>
                                    <AdWrapper type={AdTypes.LEADERBOARD_ONE} data-testid="event-profile.header.ad.leaderboard" />
                                </div>
                            )}
                            <h6 className="container pb0 pt0 subtitle text-medium text-gray text-uppercase mb24" data-testid="event-profile.header.metadata-subtitle">
                                {(isUserRegisteredForEvent || showCompleteRegistrationBtn) && (
                                    <div className="d-block d-md-inline-block">
                                        <span className={`text-black  ${styles.metadataStyle}`}>{statusMessage}</span>
                                        <span className={`d-none d-md-inline-block middleDot`}>&nbsp;</span>
                                    </div>
                                )}
                                <span>{event.data?.eventSchedule.eventDatesShort}</span>
                                {!!event.data.eventLocations?.length && (
                                    <>
                                        <span className={`d-none d-md-inline-block middleDot`}>&nbsp;</span>
                                        <br className="d-block d-md-none" />
                                        <span>{event.data.eventLocations[0].placeName || event.data.eventLocations[0].display} </span>
                                    </>
                                )}
                                <span className={`d-none d-md-inline-block middleDot`}>&nbsp;</span>
                                <br className="d-block d-md-none" />
                                {event.data?.priceRange && (
                                    <span>{event.data?.priceRange}</span>
                                )}
                                {event.data?.hasPrizeMoney && event.data.eventTours.length > 0 && (
                                    <>
                                        <br />
                                        <span>{event.data.prizeMoneyText}</span>
                                        <span className={`d-none d-md-inline-block middleDot`}>&nbsp;</span>
                                        <br className="d-block d-md-none" />
                                        <span>
                                            {event.data.eventTours.map((tour, i) => (
                                                <span key={`${tour.id}`}>{tour.name}{i < event.data.eventTours.length - 1 ? ', ' : ''}</span>
                                            ))}
                                        </span>
                                    </>
                                )}
                                {event.data?.hasPrizeMoney && event.data.eventTours.length <= 0 && (
                                    <>
                                        <span className={`d-none d-md-inline-block middleDot`}>&nbsp;</span>
                                        <br className="d-block d-md-none" />
                                        <span>{event.data.prizeMoneyText}</span>
                                    </>
                                )}
                                {renderDivisionEnvironmentsAndSurfaces()}
                                {event.data && event.data.eventTours?.length > 0 && !event.data.hasPrizeMoney && (
                                    <>
                                        <span className={`d-none d-md-inline-block middleDot`}>&nbsp;</span>
                                        <br className="d-block d-md-none" />
                                        <span>
                                            {event.data.eventTours.map((tour, i) => (
                                                <span key={`${tour.id}`}>{tour.name}{i < event.data.eventTours.length - 1 ? ', ' : ''}</span>
                                            ))}
                                        </span>
                                    </>
                                )}
                                {isPaidHitEvent && event.data.eventDivisions[0]?.utrRange && (
                                    <>
                                        <span className={`d-none d-md-inline-block middleDot`}>&nbsp;</span>
                                        <br className="d-block d-md-none" />
                                        <span data-testid="event-profile.header.utr-range-text">
                                            UTR {event.data.eventDivisions[0]?.utrRange}
                                        </span>
                                    </>
                                )}
                                <br className="d-block d-md-none" />
                                {shouldShowAddToCalendar() && <AddEventToCalendar uiElement={'link'} eventDetails={event.data} loggedInUserId={auth?.user?.id} />}
                            </h6>
                        </div>
                    </div>
                </div>
                <div className={`container pb0 pt0`}>
                    <div className={`row`}>
                        <div className={`col-12 ${collapsed ? 'd-none' : ''}`}>
                            {isClubActive(event.data.club) && (
                                isPaidHitEvent ? (
                                    <PaidHitRegistrationButtonV2
                                        isCurrentMemberAdmin={isCurrentMemberAdmin}
                                        handleRegClick={handleRegClick}
                                    />
                                ) : (
                                    <RegistrationCtaV3
                                        isCurrentMemberAdmin={isCurrentMemberAdmin}
                                        handleRegClick={handleRegClick}
                                        handleTabClick={handleTabClick}
                                        sportTypeId={sportTypeId}
                                        previewOnly={previewOnly}
                                        showShareUrlPopup={showShareUrlPopup}
                                        isRegistrationIncomplete={isRegistrationIncomplete}
                                        hasPendingAdminInvitation={hasPendingAdminInvitation}
                                        handleFindPlayerClick={handleFindPlayerClick}
                                    />
                                )
                            )}
                        </div>
                    </div>
                    <div className={`row ${collapsed ? 'd-none' : ''}`}>
                        <div className="col btn-tabs">
                            <TabBar
                                tabs={tabs}
                                handleTabClickV3={handleTabClick}
                                showLabels={false}
                                eventName="Event Tab Clicked"
                                clickCategory="Event Profile"
                                clickLocation="Header Tabs"
                                transparent
                                data-testid="event-profile.header.tab-bar"
                                noContainer
                                noBorder
                                noPadding
                            />
                        </div>
                    </div>
                </div>
            </div>
            {showSafeSportPopup && (
                <Popup
                    className="popup-md"
                    onCloseClick={() => {
                        setShowSafeSportPopup(false);
                    }}
                    config={{
                        fullScreen: false,
                        noPadding: true,
                        scrollable: false,
                    }}
                >
                    <div className="popup-section p24 text-dark">
                        <p data-testid="event-profile.header.safesport-popup-text">
                            This organizer has completed training with the U.S. Center for SafeSport. Learn more at <a href={'https://uscenterforsafesport.org/'} rel="noopener noreferrer" target="_blank" data-testid="event-profile.header.safesport-popup-link">uscenterforsafesport.org</a>.
                        </p>
                    </div>
                </Popup>
            )}
        </>
    );
}

EventProfileHeaderV3.propTypes = {
    isCurrentMemberAdmin: PropTypes.bool,
    collapsed: PropTypes.bool,
    tabs: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        show: PropTypes.bool,
    })).isRequired,
    previewOnly: PropTypes.bool,
    sportTypeId: PropTypes.number.isRequired,
    showShareUrlPopup: PropTypes.func,
    handleRegClick: PropTypes.func,
    handleTabClick: PropTypes.func,
    isRegistrationIncomplete: PropTypes.bool,
    hasPendingAdminInvitation: PropTypes.bool,
    handleFindPlayerClick: PropTypes.func,
    isPttEvent: PropTypes.bool,
    isCurrentMemberClubOrganizer: PropTypes.bool,
};

EventProfileHeaderV3.defaultProps = {
    isCurrentMemberAdmin: false,
    collapsed: false,
    previewOnly: false,
    showShareUrlPopup: () => { },
    handleRegClick: () => { },
    handleTabClick: () => { },
    isRegistrationIncomplete: false,
    hasPendingAdminInvitation: false,
    handleFindPlayerClick: () => { },
    isPttEvent: false,
    isCurrentMemberClubOrganizer: false,
};

export default withRouter(EventProfileHeaderV3);
